/**
 * Pure helpers for the Locations pages: paths, ids, errors, times, addresses, numbers.
 *
 * LocationID 0 ("Online Campus") is a real location: never test ids for truthiness or `> 0`.
 */

import {
  LOCATION_CONFLICT_CODES,
  LOCATION_DAYS_OF_WEEK,
  type Address,
  type Location,
  type LocationLeader,
  type LocationModelView,
  type LocationVenue,
  type ServiceTime,
} from '@actschurch/ccms-sdk';

// ---------------------------------------------------------------- paths + ids

export const LOCATIONS_BASE_PATH = '/app/locations';
export const CAMPUSES_PATH = `${LOCATIONS_BASE_PATH}/campuses`;

export type LocationTab =
  | 'address'
  | 'service-times'
  | 'venues'
  | 'leaders'
  | 'social-media'
  | 'info'
  | 'members';

export const locationPath = (locationId: number, tab?: LocationTab) =>
  `${CAMPUSES_PATH}/${locationId}${tab ? `/${tab}` : ''}`;
export const locationEditPath = (locationId: number) => `${CAMPUSES_PATH}/${locationId}/edit`;
export const NEW_LOCATION_PATH = `${CAMPUSES_PATH}/new`;

/** Meetings admin venues page (set-ups, layouts, blackouts) with a venue preselected. */
export const meetingVenuesPath = (tab: 'venues' | 'layouts' | 'blackouts', venueId?: number, locationId?: number) => {
  const params = new URLSearchParams({ tab });
  if (venueId != null) params.set('venue', String(venueId));
  if (locationId != null) params.set('location', String(locationId));
  return `/app/meetings/admin/venues?${params.toString()}`;
};

/** Admin member profile (Admin or Member.Read, same gate as the member endpoints). */
export const memberProfilePath = (memberId: number) => `/admin/members/${memberId}`;

/** Route param → location id. "0" is valid; anything that is not a whole number is null. */
export function parseLocationId(param: string | undefined | null): number | null {
  if (param == null || !/^\d+$/.test(param)) return null;
  const n = Number(param);
  return Number.isSafeInteger(n) ? n : null;
}

/** Select value ("0", "12" or null) → id. */
export function selectToId(value: string | null | undefined): number | null {
  return parseLocationId(value ?? null);
}

// ---------------------------------------------------------------- errors

interface SdkErrorShape {
  message?: string;
  status?: number;
  statusCode?: number;
  errors?: unknown;
}

export function errorStatus(err: unknown): number | undefined {
  const e = err as SdkErrorShape | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

function errorList(err: unknown): string[] {
  const e = err as SdkErrorShape | null;
  return Array.isArray(e?.errors) ? (e!.errors as unknown[]).filter((x): x is string => typeof x === 'string') : [];
}

/** 409 conflict code (errors[0]), e.g. "location_name_taken". */
export function conflictCode(err: unknown): string | undefined {
  if (errorStatus(err) !== 409) return undefined;
  return errorList(err)[0];
}

/** 400 field errors: "field: message" → { field: message }. Field names are the request's camelCase names. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (errorStatus(err) !== 400) return {};
  const out: Record<string, string> = {};
  for (const item of errorList(err)) {
    const idx = item.indexOf(':');
    if (idx > 0) {
      const key = item.slice(0, idx).trim();
      out[key] = out[key] ? `${out[key]} ${item.slice(idx + 1).trim()}` : item.slice(idx + 1).trim();
    }
  }
  return out;
}

/** 400 messages that carry no field name. */
function generalErrors(err: unknown): string[] {
  if (errorStatus(err) !== 400) return [];
  return errorList(err).filter((item) => item.indexOf(':') <= 0);
}

const CONFLICT_HINTS: Record<string, string> = {
  [LOCATION_CONFLICT_CODES.LocationNameTaken]: 'Another location already uses this name.',
  [LOCATION_CONFLICT_CODES.LocationArchived]: 'This location is archived. Restore it before making changes.',
  [LOCATION_CONFLICT_CODES.AddressExists]: 'This location already has an address. Reload to edit it.',
  [LOCATION_CONFLICT_CODES.ServiceTimeExists]: 'There is already a service on this day at this start time.',
  [LOCATION_CONFLICT_CODES.ServiceTimeArchived]: 'This service time is archived. Restore it before editing.',
  [LOCATION_CONFLICT_CODES.VenueNameTaken]: 'This location already has a venue with this name.',
  [LOCATION_CONFLICT_CODES.VenueArchived]: 'This venue is archived. Restore it before editing.',
  [LOCATION_CONFLICT_CODES.LeaderExists]: 'This person is already a leader at this location.',
  [LOCATION_CONFLICT_CODES.InfoNameTaken]: 'This location already has an entry with this key.',
  [LOCATION_CONFLICT_CODES.SocialAccountExists]: 'This location already has an account on this platform.',
  [LOCATION_CONFLICT_CODES.PlatformNameTaken]: 'A platform with this name already exists.',
  [LOCATION_CONFLICT_CODES.PlatformInUse]: 'Locations still have accounts on this platform. Remove those accounts first.',
};

/**
 * One readable sentence for a failed call: the server's message for 409s (it names the clash),
 * the field messages for 400s, a plain explanation for 403 / 404 / network failures.
 */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const status = errorStatus(err);
  const e = err as SdkErrorShape;
  if (status === 409) {
    const code = conflictCode(err);
    return e.message || (code && CONFLICT_HINTS[code]) || fallback;
  }
  if (status === 400) {
    const fields = Object.values(fieldErrors(err));
    const general = generalErrors(err);
    const parts = [...general, ...fields];
    return parts.length ? parts.join(' ') : e.message || fallback;
  }
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do this.';
  if (status === 404) return e.message || 'It no longer exists. It may have been removed by someone else.';
  if (status === 408) return 'The server took too long to answer. Check your connection and try again.';
  return e.message || fallback;
}

/** Hint for a 409 code, when the server message is too terse. */
export function conflictHint(err: unknown): string | undefined {
  const code = conflictCode(err);
  return code ? CONFLICT_HINTS[code] : undefined;
}

// ---------------------------------------------------------------- stamps

/** yyyyMMddHHmmss (or yyyyMMdd) number → Date. */
export function stampToDate(stamp: number | string | null | undefined): Date | null {
  if (stamp == null) return null;
  const s = String(stamp).replace(/\D/g, '');
  if (s.length < 8 || /^0+$/.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const mo = Number(s.slice(4, 6)) - 1;
  const d = Number(s.slice(6, 8));
  const h = s.length >= 10 ? Number(s.slice(8, 10)) : 0;
  const mi = s.length >= 12 ? Number(s.slice(10, 12)) : 0;
  const date = new Date(y, mo, d, h, mi);
  return Number.isNaN(date.getTime()) || y < 1900 ? null : date;
}

export function formatStamp(stamp: number | string | null | undefined, fallback = 'Not recorded'): string {
  const d = stampToDate(stamp);
  return d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : fallback;
}

export function formatStampDateTime(stamp: number | string | null | undefined, fallback = 'Not recorded'): string {
  const d = stampToDate(stamp);
  return d
    ? d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : fallback;
}

// ---------------------------------------------------------------- numbers + text

const numberFormat = new Intl.NumberFormat('en-ZA');
export const formatNumber = (n: number | null | undefined) => numberFormat.format(Number(n ?? 0));

export const plural = (n: number, one: string, many?: string) => `${formatNumber(n)} ${n === 1 ? one : many ?? `${one}s`}`;

/** Join with commas and a final "and" (no serial comma). */
export function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function isHexColor(value: string | null | undefined): value is string {
  return !!value && /^#[0-9a-f]{6}$/i.test(value.trim());
}

/** Location / service colour or null when unset or not a hex value. */
export const colorOrNull = (value: string | null | undefined) => (isHexColor(value) ? value.trim() : null);

// ---------------------------------------------------------------- status

export type LocationStatus = 'Active' | 'Inactive' | 'Archived';

export function locationStatus(location: Pick<Location, 'isActive'> & { isDeleted?: boolean | null }): LocationStatus {
  if (location.isDeleted) return 'Archived';
  return location.isActive ? 'Active' : 'Inactive';
}

export const STATUS_COLORS: Record<LocationStatus, string> = { Active: 'green', Inactive: 'gray', Archived: 'orange' };

export const byName = <T extends { name?: string | null }>(a: T, b: T) =>
  (a.name ?? '').localeCompare(b.name ?? '', 'en', { sensitivity: 'base' });

// ---------------------------------------------------------------- times + days

/** Day value of a service time; legacy rows without a day count as Sunday (the server does the same). */
export const serviceDay = (st: Pick<ServiceTime, 'dayOfWeek'>) =>
  st.dayOfWeek != null && st.dayOfWeek >= 0 && st.dayOfWeek <= 6 ? st.dayOfWeek : 0;

export const dayLabel = (day: number) => LOCATION_DAYS_OF_WEEK.find((d) => d.value === day)?.label ?? 'Sunday';
export const dayShort = (day: number) => LOCATION_DAYS_OF_WEEK.find((d) => d.value === day)?.short ?? 'Sun';

export const DAY_OPTIONS = LOCATION_DAYS_OF_WEEK.map((d) => ({ value: String(d.value), label: d.label }));

/** "HH:mm:ss" / "H:mm" → "HH:mm" (or '' when unparseable). */
export function toHHmm(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return '';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

export const timeToMinutes = (value: string | null | undefined): number | null => {
  const hhmm = toHHmm(value);
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** "09:00 to 10:30", or "From 09:00" when open-ended. */
export function formatServiceRange(st: Pick<ServiceTime, 'startTime' | 'endTime'>): string {
  const start = toHHmm(st.startTime);
  const end = toHHmm(st.endTime);
  if (!start) return end ? `Until ${end}` : 'No time set';
  return end ? `${start} to ${end}` : `From ${start}`;
}

/** Service times sorted by day then start time. */
export function sortServiceTimes<T extends Pick<ServiceTime, 'dayOfWeek' | 'startTime'>>(rows: T[]): T[] {
  return rows
    .slice()
    .sort((a, b) => serviceDay(a) - serviceDay(b) || (timeToMinutes(a.startTime) ?? 0) - (timeToMinutes(b.startTime) ?? 0));
}

/** Compact weekly summary, e.g. "Sun 09:00, 11:00 · Wed 18:30". */
export function weeklySummary(rows: ServiceTime[]): string {
  const active = sortServiceTimes(rows.filter((r) => r.isActive && !r.isDeleted));
  if (active.length === 0) return '';
  const byDay = new Map<number, string[]>();
  for (const st of active) {
    const day = serviceDay(st);
    byDay.set(day, [...(byDay.get(day) ?? []), toHHmm(st.startTime) || '?']);
  }
  return Array.from(byDay.entries())
    .map(([day, times]) => `${dayShort(day)} ${times.join(', ')}`)
    .join(' · ');
}

// ---------------------------------------------------------------- addresses

export function parseCoordinate(value: string | null | undefined): number | null {
  if (value == null) return null;
  const trimmed = String(value).trim().replace(',', '.');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function addressCoordinates(address: Address | null | undefined): { lat: number; lng: number } | null {
  if (!address) return null;
  const lat = parseCoordinate(address.geoLocationA);
  const lng = parseCoordinate(address.geoLocationL);
  if (lat == null || lng == null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

const clean = (v: string | null | undefined) => (v ?? '').trim();

/** Address as display lines (building name, street lines, suburb, city + code, province, country). */
export function addressLines(address: Address | null | undefined): string[] {
  if (!address) return [];
  const cityLine = [clean(address.city), clean(address.code)].filter(Boolean).join(' ');
  return [
    clean(address.name),
    clean(address.addressLine1),
    clean(address.addressLine2),
    clean(address.addressLine3),
    clean(address.suburb),
    cityLine,
    clean(address.province),
    clean(address.country),
  ].filter(Boolean);
}

/** One line: street, suburb, city, country (falls back to the building name). */
export function addressSummary(address: Address | null | undefined): string {
  if (!address) return '';
  const parts = [clean(address.addressLine1), clean(address.suburb), clean(address.city), clean(address.country)].filter(Boolean);
  return parts.length ? parts.join(', ') : clean(address.name);
}

/** "City, Country" (or whatever exists). */
export function cityCountry(address: Address | null | undefined): string {
  if (!address) return '';
  return [clean(address.city), clean(address.country)].filter(Boolean).join(', ');
}

/** What an address is missing for riders, maps and printed material. Empty = complete. */
export function addressGaps(address: Address | null | undefined): string[] {
  if (!address) return ['address'];
  const gaps: string[] = [];
  if (!clean(address.addressLine1)) gaps.push('street');
  if (!clean(address.city)) gaps.push('city');
  if (!clean(address.country)) gaps.push('country');
  if (!addressCoordinates(address)) gaps.push('map position');
  return gaps;
}

export function mapsSearchUrl(address: Address | null | undefined): string | null {
  const coords = addressCoordinates(address);
  if (coords) return `https://www.google.com/maps?q=${coords.lat},${coords.lng}`;
  const text = addressLines(address).join(', ');
  return text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}` : null;
}

export function osmEmbedUrl(lat: number, lng: number): string {
  const d = 0.006;
  const bbox = [lng - d, lat - d * 0.6, lng + d, lat + d * 0.6].map((n) => n.toFixed(6)).join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/**
 * Pull "lat, lng" out of pasted text: plain coordinates, a Google Maps link (@lat,lng / q=lat,lng /
 * !3dlat!4dlng) or an OpenStreetMap link (mlat / mlon).
 */
export function parsePastedCoordinates(text: string): { lat: string; lng: string } | null {
  const t = text.trim();
  const patterns = [
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    /[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /mlat=(-?\d+(?:\.\d+)?)&mlon=(-?\d+(?:\.\d+)?)/,
    /^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/,
  ];
  for (const p of patterns) {
    const m = p.exec(t);
    if (m) {
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat: m[1], lng: m[2] };
    }
  }
  return null;
}

// ---------------------------------------------------------------- leaders

export interface LeaderState {
  count: number;
  primaryIds: number[];
  /** Exactly one primary. */
  ok: boolean;
  noLeaders: boolean;
  noPrimary: boolean;
  multiplePrimaries: boolean;
}

export function leaderState(leaders: LocationLeader[] | null | undefined): LeaderState {
  const rows = leaders ?? [];
  const primaryIds = rows.filter((l) => l.isPrimary).map((l) => l.memberID);
  return {
    count: rows.length,
    primaryIds,
    ok: primaryIds.length === 1,
    noLeaders: rows.length === 0,
    noPrimary: rows.length > 0 && primaryIds.length === 0,
    multiplePrimaries: primaryIds.length > 1,
  };
}

// ---------------------------------------------------------------- venues

/** Zoom / Teams / WhatsApp style rooms (production has them on the Online Campus). */
export function isOnlineVenue(venue: Pick<LocationVenue, 'name' | 'description'>): boolean {
  return /\b(zoom|teams|whats ?app|online|virtual|youtube|livestream|live stream|stream|google meet|meet\.google|skype)\b/i.test(
    `${venue.name ?? ''} ${venue.description ?? ''}`
  );
}

export function formatCapacity(venue: Pick<LocationVenue, 'minCapacity' | 'maxCapacity'>): string {
  const min = Number(venue.minCapacity ?? 0);
  const max = Number(venue.maxCapacity ?? 0);
  if (max <= 0 && min <= 0) return 'Not set';
  if (min > 0 && max > 0 && min !== max) return `${formatNumber(min)} to ${formatNumber(max)}`;
  if (max > 0) return `Up to ${formatNumber(max)}`;
  return `From ${formatNumber(min)}`;
}

/** Legacy rows may hold min > max; the server only re-checks capacities when they are sent. */
export const capacityLooksWrong = (venue: Pick<LocationVenue, 'minCapacity' | 'maxCapacity'>) =>
  Number(venue.minCapacity ?? 0) < 0 || Number(venue.maxCapacity ?? 0) < 0 || (venue.maxCapacity > 0 && venue.minCapacity > venue.maxCapacity);

// ---------------------------------------------------------------- needs attention

export interface LocationIssue {
  key: 'address' | 'address-gaps' | 'service-times' | 'no-leaders' | 'no-primary' | 'multiple-primaries';
  label: string;
  tab: LocationTab;
  severity: 'high' | 'medium';
}

/** Gaps that stop a campus from showing well elsewhere (Carpool, Meetings, member profiles…). */
export function locationIssues(view: LocationModelView): LocationIssue[] {
  const issues: LocationIssue[] = [];
  const hasAddress = view.hasAddress ?? !!view.address;
  if (!hasAddress) {
    issues.push({ key: 'address', label: 'No address', tab: 'address', severity: 'high' });
  } else {
    const gaps = addressGaps(view.address);
    if (gaps.length) issues.push({ key: 'address-gaps', label: `Address has no ${joinAnd(gaps)}`, tab: 'address', severity: 'medium' });
  }
  const activeServices = (view.serviceTimes ?? []).filter((s) => s.isActive && !s.isDeleted).length;
  if (activeServices === 0) issues.push({ key: 'service-times', label: 'No active service times', tab: 'service-times', severity: 'high' });
  const leaders = leaderState(view.leaders);
  if (leaders.noLeaders) issues.push({ key: 'no-leaders', label: 'No leaders', tab: 'leaders', severity: 'high' });
  else if (leaders.noPrimary) issues.push({ key: 'no-primary', label: 'No primary leader', tab: 'leaders', severity: 'high' });
  else if (leaders.multiplePrimaries)
    issues.push({ key: 'multiple-primaries', label: `${leaders.primaryIds.length} leaders marked primary`, tab: 'leaders', severity: 'medium' });
  return issues;
}

// ---------------------------------------------------------------- members

export function personName(m: { firstName?: string | null; middleName?: string | null; lastName?: string | null; memberID?: number } | null | undefined): string {
  if (!m) return '';
  return [m.firstName, m.lastName].map((x) => (x ?? '').trim()).filter(Boolean).join(' ');
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
